import { ComponentProps } from 'react';

import { VIDEO_FORMAT, Video } from 'atoms/ui/Video';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';

import introLoop from './assets/16x9/intro-loop.webm';
import introToEnd from './assets/16x9/intro-to-end.webm';
import intro from './assets/16x9/intro.mp4';
import outro from './assets/16x9/outro.mp4';
import toIntro from './assets/16x9/to-intro.webm';
import tunnel from './assets/16x9/tunnel.mp4';
import introLoopMobile from './assets/9x16/intro-loop.webm';
import introToEndMobile from './assets/9x16/intro-to-end.webm';
import introMobile from './assets/9x16/intro.mp4';
import outroMobile from './assets/9x16/outro.mp4';
import toIntroMobile from './assets/9x16/to-intro.webm';
import tunnelMobile from './assets/9x16/tunnel.mp4';

/* Order:
 * to-intro
 * intro-loop
 * intro
 * tunnel (x times = x steps)
 * outro (when close button is clicked)
 */

const DESKTOP_VIDEOS = {
  toIntro,
  introLoop,
  intro,
  introToEnd,
  outro,
  tunnel,
};

const MOBILE_VIDEOS = {
  toIntro: toIntroMobile,
  introLoop: introLoopMobile,
  intro: introMobile,
  introToEnd: introToEndMobile,
  outro: outroMobile,
  tunnel: tunnelMobile,
};

/* https://gitlab.com/sorare/frontend/-/blob/master/TROUBLESHOOTING.md?ref_type=heads#video-is-not-displaying-in-safari-ios */
const MOBILE_VIDEOS_MP4 = {
  toIntro:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/to-intro.mp4',
  introLoop:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-loop.mp4',
  intro:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro.mp4',
  introToEnd:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-out.mp4',
  outro:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/outro.mp4',
  tunnel:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/tunnel.mp4',
};

const desktopVideosSources = Object.entries(DESKTOP_VIDEOS).reduce(
  (acc, [key, src]) => {
    acc[key as keyof typeof DESKTOP_VIDEOS] = [
      {
        src,
        type: VIDEO_FORMAT.WEBM,
      },
    ];
    return acc;
  },
  {} as {
    [key in keyof typeof DESKTOP_VIDEOS]: ComponentProps<
      typeof Video
    >['sources'];
  }
);

const mobileVideoSources = Object.entries(MOBILE_VIDEOS).reduce(
  (acc, [key, src]) => {
    acc[key as keyof typeof MOBILE_VIDEOS] = [
      {
        src,
        type: VIDEO_FORMAT.WEBM,
      },
      {
        src: MOBILE_VIDEOS_MP4[key as keyof typeof MOBILE_VIDEOS],
        type: VIDEO_FORMAT.MP4,
      },
    ];
    return acc;
  },
  {} as {
    [key in keyof typeof MOBILE_VIDEOS]: ComponentProps<
      typeof Video
    >['sources'];
  }
);

export const useResultsStoryAssets = () => {
  const isTabletAndAbove = useIsTabletAndAbove();
  return isTabletAndAbove ? desktopVideosSources : mobileVideoSources;
};
